import { Injectable } from '@angular/core';
import { defaultPageSize } from '../constants/default';
import { DialogPositionSize } from '../model';

const baseKey = 'localPreference';
const pageSizeKey = `${baseKey}-pageSize`;
const dialogPositionSizeKeyPrefix = `${baseKey}-dialog-positionSize`;
const panelCollapsedKeyPrefix = `${baseKey}-panel-collapsed`;

@Injectable({
    providedIn: 'root',
})
export class LocalPreferenceService {
    constructor() {}

    storePageSize(value: number): void {
        localStorage.setItem(pageSizeKey, `${value}`);
    }

    get pageSize(): number {
        const value = localStorage.getItem(pageSizeKey);
        const parsed = parseInt(value ?? '', 10);
        return isNaN(parsed) ? defaultPageSize : parsed;
    }

    /**
     * Stores the position and size of a dialog.
     * @param dialogKey - Unique identifier for the dialog
     * @param value - The position and size to store
     */
    storeDialogPositionSize(dialogKey: string, value: DialogPositionSize): void {
        const key = `${dialogPositionSizeKeyPrefix}-${dialogKey}`;
        localStorage.setItem(key, JSON.stringify(value));
    }

    /**
     * Retrieves the stored position and size of a dialog.
     * @param dialogKey - Unique identifier for the dialog
     * @returns The stored position and size, or null if not found
     */
    getDialogPositionSize(dialogKey: string): DialogPositionSize | null {
        const key = `${dialogPositionSizeKeyPrefix}-${dialogKey}`;
        const value = localStorage.getItem(key);
        if (!value) {
            return null;
        }
        try {
            return JSON.parse(value) as DialogPositionSize;
        } catch {
            return null;
        }
    }

    /**
     * Stores whether a panel is collapsed.
     * @param panelKey - Unique identifier for the panel
     * @param value - Whether the panel is collapsed
     */
    storePanelCollapsed(panelKey: string, value: boolean): void {
        const key = `${panelCollapsedKeyPrefix}-${panelKey}`;
        localStorage.setItem(key, `${value}`);
    }

    /**
     * Retrieves the stored collapsed state of a panel.
     * @param panelKey - Unique identifier for the panel
     * @param defaultValue - The value to return when nothing is stored
     * @returns Whether the panel is collapsed
     */
    getPanelCollapsed(panelKey: string, defaultValue: boolean): boolean {
        const key = `${panelCollapsedKeyPrefix}-${panelKey}`;
        const value = localStorage.getItem(key);
        return value == null ? defaultValue : value === 'true';
    }
}
